import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { PostService } from '../../services/post.service';
import { IPostUpdate } from 'src/app/models/post-update.interface';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ISocialCardResult } from 'src/app/models/social-card-result';
import { IonicModule, IonTextarea } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TEMPLATE_GROUPS, TemplateGroup } from 'src/app/models/template-groups';
import { ImagePlacement, ImageTemplate, getDefaultImagePlacementForTemplate } from 'src/app/models/account.interface';

@Component({
  selector: 'app-edit-social-card',
  templateUrl: './edit-social-card.component.html',
  styleUrls: ['./edit-social-card.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule]
})
export class EditSocialCardComponent implements OnChanges {
  @Input() post: IPostUpdate | undefined;
  templateGroups: TemplateGroup[] = TEMPLATE_GROUPS;
  imagePlacements: { id: ImagePlacement, label: string }[] = [
    { id: 'attention', label: 'Smart Crop' },
    { id: 'center', label: 'Fill: Center' },
    { id: 'top', label: 'Fill: Top' },
    { id: 'bottom', label: 'Fill: Bottom' },
    { id: 'fit-blur', label: 'Fit (Blur)' },
    { id: 'fit-color', label: 'Fit (Color)' },
    { id: 'fit-theme', label: 'Fit (Theme)' },
  ];

  isEditingTitle = false;
  editedTitle: string | undefined;

  isGenerating = false;
  private socialCardUpdate$ = new Subject<void>();

  @ViewChild('titleInput', { static: false }) titleInput?: IonTextarea;

  constructor(private postService: PostService) {
    this.socialCardUpdate$
      .pipe(
        debounceTime(1000), // Wait for 1s of inactivity before firing
        tap(() => (this.isGenerating = true)),
        // Use switchMap but catch errors inside to keep the stream alive
        switchMap(() => {
          if (!this.post) {
            // No post yet; stop generation gracefully without erroring the stream
            this.isGenerating = false;
            return EMPTY;
          }
          return this.postService.socialCard(this.post).pipe(
            catchError((err) => {
              console.error('Failed to update social card', err);
              this.isGenerating = false;
              return EMPTY; // Keep stream alive for subsequent edits
            })
          );
        })
      )
      .subscribe({
        next: (result: ISocialCardResult) => {
          if (this.post && this.post.socialCard && result) {
            // Update the social card with the new data from the server
            // Merge images so we don't lose other layouts (e.g., portrait) if API returns one layout only
            if (result.images) {
              const existing = this.post.socialCard.images || {};
              this.post.socialCard.images = { ...existing, ...result.images };
            }
            if (result.title) {
              this.post.socialCard.title = result.title;
              this.editedTitle = result.title;
            }
            if (result.blurb) {
              this.post.socialCard.blurb = result.blurb;
            }
            if (result.template) {
              this.post.socialCard.template = result.template;
            }
          }
          this.isGenerating = false;
        },
        error: (err) => {
          // This should rarely fire now since errors are handled inside switchMap
          console.error('Failed to update social card', err);
          this.isGenerating = false;
        },
      });
  }

  get socialCardPreviewUrl(): string | undefined {
    if (this.post?.socialCard?.images) {
      const images = this.post.socialCard.images;
      return images['landscape'] || images['portrait'] || Object.values(images)[0];
    }
    return undefined;
  }

  startEditingTitle(): void {
    this.isEditingTitle = true;
    this.editedTitle = this.post?.socialCard?.title;
  }

  saveTitle(): void {
    if (this.post?.socialCard) {
      this.post.socialCard.title = this.editedTitle;
    }
    this.isEditingTitle = false;
    this.onModelChange();
  }

  cancelEditingTitle(): void {
    this.isEditingTitle = false;
    // No need to restore title, as ngModel is on a separate property
  }

  shouldShowBlurb(): boolean {
    if (!this.post?.socialCard?.template) {
      return false;
    }
    const templateId = this.post.socialCard.template;
    for (const group of this.templateGroups) {
      const template = group.items.find(item => item.id === templateId);
      if (template) {
        return !!template.usesBlurb;
      }
    }
    return false;
  }

  ngOnInit(): void {
    // Ensure socialCard object exists when component initializes
    if (this.post && !this.post.socialCard) {
      this.post.socialCard = {
        title: '',
        blurb: '',
        template: 'title-blurb', // Default template expected by backend
        images: {},
        imagePlacement: getDefaultImagePlacementForTemplate('title-blurb')
      };
    } else {
      this.ensureTemplateDefaultPlacement();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['post'] && this.post) {
      // Ensure socialCard object exists
      if (!this.post.socialCard) {
        this.post.socialCard = {
          title: '',
          blurb: '',
          template: 'title-blurb', // Default template expected by backend
          images: {},
          imagePlacement: getDefaultImagePlacementForTemplate('title-blurb')
        };
      } else {
        this.ensureTemplateDefaultPlacement();
      }
    }
  }

  onModelChange(): void {
    this.socialCardUpdate$.next();
  }

  /**
   * Public method to trigger generation
   */
  generateCard(): void {
    this.onModelChange();
  }

  generateTitleWithAI(): void {
    if (this.post && this.post.socialCard) {
      this.post.socialCard.title = ''; // Clear title to signal AI generation
      this.editedTitle = '';
      this.onModelChange();
    }
  }


    getSocialCardPreviewUrl(): string | undefined {
    if (this.post?.socialCard?.images) {
      // Prefer landscape for a generic preview, otherwise take the first available one.
      return this.post.socialCard.images['landscape'] || this.post.socialCard.images['portrait'] || Object.values(this.post.socialCard.images)[0];
    }
    return undefined;
  }

  focusTitleEditor(): void {
    // Ensure we are in edit mode
    if (!this.isEditingTitle) {
      this.startEditingTitle();
    }

    // Wait for the textarea to render, then scroll and focus
    setTimeout(() => {
      const textarea = this.titleInput;
      if (!textarea) return;

      // Focus the input
      textarea.setFocus();

      // Also scroll the native input into view smoothly
      textarea.getInputElement().then((el) => {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          // Fallback if scrollIntoView options unsupported
          el.scrollIntoView();
        }
      });
    }, 50);
  }

  private ensureTemplateDefaultPlacement(): void {
    if (!this.post?.socialCard) {
      return;
    }

    const template = this.post.socialCard.template as ImageTemplate | undefined;
    if (!template || this.post.socialCard.imagePlacement) {
      return;
    }

    this.post.socialCard.imagePlacement = getDefaultImagePlacementForTemplate(template);
  }
}